import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { FiPlus, FiEdit2, FiTrash2 } from 'react-icons/fi';
import {
  adminCakeOptionGroups,
  adminCakeOptionItems,
  CakeOptionGroup,
  CakeOptionGroupInput,
  CakeOptionItem,
  CakeOptionItemInput,
  CakeOptionSelectionMode,
} from '../../api/adminCakeOptions';
import { adminCategories } from '../../api/admin';
import { formatCurrency } from '../../utils/currency';
import { getErrorDetail, resolveErrorBanner, ErrorBannerState } from '../../utils/errorDetail';

// Global Custom Cake option groups (task-191). Groups are linked to cake
// categories (sub-categories inherit); every product in a linked category gets
// the group's items at their default price unless overridden per product (see
// the product editor's Cake Options panel).

// Mirrors backend CC_NAME_MAX_LENGTH (schemas/cc_options.py).
const CC_NAME_MAX_LENGTH = 100;
const GROUPS_KEY = ['cakeOptionGroups'];
const CATEGORIES_KEY = ['cakeOptionCategories'];

// ─── Styled components (same look as AdminBYOOptions) ────────────────────────

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;
const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;
const Title = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #333;
`;
const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;
const IconButton = styled.button<{ $variant?: 'edit' | 'delete' }>`
  padding: 8px;
  background: ${p => (p.$variant === 'delete' ? '#fee2e2' : '#ede9fe')};
  color: ${p => (p.$variant === 'delete' ? '#dc2626' : '#667eea')};
  border: none;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
const CancelButton = styled.button`
  padding: 10px 20px;
  background: #f3f4f6;
  color: #6b7280;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
`;
const Section = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
  overflow: hidden;
`;
const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 18px 24px;
  flex-wrap: wrap;
`;
const SectionTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;
const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 0;
`;
const Badge = styled.span<{ $tone?: 'blue' | 'amber' | 'grey' }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  background: ${p => (p.$tone === 'blue' ? '#dbeafe' : p.$tone === 'amber' ? '#fef3c7' : '#f3f4f6')};
  color: ${p => (p.$tone === 'blue' ? '#1d4ed8' : p.$tone === 'amber' ? '#92400e' : '#6b7280')};
`;
const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;
const CategorySummary = styled.div`
  padding: 0 24px 14px;
  font-size: 13px;
  color: #6b7280;
`;
const TableWrapper = styled.div`
  overflow-x: auto;
  border-top: 1px solid #e5e7eb;
`;
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;
const Th = styled.th`
  padding: 12px 16px;
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
`;
const Td = styled.td`
  padding: 12px 16px;
  font-size: 14px;
  color: #333;
  border-bottom: 1px solid #f3f4f6;
  vertical-align: middle;
`;
const EmptyCell = styled.td`
  padding: 32px 16px;
  text-align: center;
  color: #9ca3af;
  font-size: 14px;
`;
const ActionCell = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;
const Modal = styled.div`
  display: flex;
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  align-items: center;
  justify-content: center;
  z-index: 2000;
`;
const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  max-width: 520px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  padding: 24px;
`;
const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;
const ModalTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #333;
`;
const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: #6b7280;
  cursor: pointer;
  line-height: 1;
`;
const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;
const FormRow = styled.div`
  display: flex;
  gap: 16px;
  ${FormGroup} {
    flex: 1;
    min-width: 0;
  }
`;
const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 6px;
`;
const Input = styled.input`
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 14px;
`;
const Select = styled.select`
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 14px;
  background: white;
`;
const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #333;
  cursor: pointer;
`;
const Hint = styled.p`
  margin: 0 0 8px;
  font-size: 12px;
  color: #6b7280;
`;
const CategoryList = styled.div`
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 8px 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;
const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
`;
const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
  color: #6b7280;
`;
const ErrorMessage = styled.div`
  background: #fee2e2;
  color: #991b1b;
  border-radius: 8px;
  padding: 16px 20px;
  font-size: 14px;
  margin-bottom: 20px;
`;
const ErrorBanner = styled(ErrorMessage)`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
`;
const DismissButton = styled.button`
  background: none;
  border: none;
  font-size: 18px;
  line-height: 1;
  color: #991b1b;
  cursor: pointer;
  padding: 0;
`;

// ─── Helpers ──────────────────────────────────────────────────────────────────

interface CategoryNode {
  id: number;
  name: string;
  depth: number;
}

// Flattens the category list into depth-first order so sub-categories render
// indented beneath their parent. Categories whose parent is missing from the
// list (e.g. inactive) are treated as roots. `seen` guards against cycles.
export const buildCategoryTree = (cats: any[]): CategoryNode[] => {
  const byParent = new Map<number | null, any[]>();
  const ids = new Set(cats.map(c => Number(c.category_id ?? c.id)));
  cats.forEach(c => {
    const parent = c.parent_category_id == null ? null : Number(c.parent_category_id);
    const key = parent !== null && ids.has(parent) ? parent : null;
    byParent.set(key, [...(byParent.get(key) ?? []), c]);
  });
  const out: CategoryNode[] = [];
  const seen = new Set<number>();
  const walk = (parent: number | null, depth: number) => {
    (byParent.get(parent) ?? []).forEach(c => {
      const id = Number(c.category_id ?? c.id);
      if (seen.has(id)) return;
      seen.add(id);
      out.push({ id, name: c.name, depth });
      walk(id, depth + 1);
    });
  };
  walk(null, 0);
  return out;
};

const isStaleConflict = (err: any): boolean =>
  err?.response?.status === 409 && /modified by someone else/i.test(getErrorDetail(err) ?? '');

interface GroupFormState {
  name: string;
  name_zh: string;
  selection_mode: CakeOptionSelectionMode;
  required: boolean;
  sort_order: number;
  active: boolean;
  category_ids: number[];
  updated_at: string | null;
}

const BLANK_GROUP_FORM: GroupFormState = {
  name: '', name_zh: '', selection_mode: 'single', required: false,
  sort_order: 0, active: true, category_ids: [], updated_at: null,
};

interface ItemFormState {
  name: string;
  name_zh: string;
  default_price: string;
  sort_order: number;
  active: boolean;
  updated_at: string | null;
}

const BLANK_ITEM_FORM: ItemFormState = {
  name: '', name_zh: '', default_price: '0', sort_order: 0, active: true, updated_at: null,
};

// ─── Page ─────────────────────────────────────────────────────────────────────

const AdminCakeOptions: React.FC = () => {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const zh = (i18n.language || '').startsWith('zh');
  const display = (name: string, nameZh?: string | null) => (zh && nameZh ? nameZh : name);

  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<CakeOptionGroup | null>(null);
  const [groupForm, setGroupForm] = useState<GroupFormState>(BLANK_GROUP_FORM);
  const [itemModalGroup, setItemModalGroup] = useState<CakeOptionGroup | null>(null);
  const [editingItem, setEditingItem] = useState<CakeOptionItem | null>(null);
  const [itemForm, setItemForm] = useState<ItemFormState>(BLANK_ITEM_FORM);
  // Raw detail + fallback key (not the resolved string) so the banner follows
  // a language toggle -- see resolveErrorBanner.
  const [banner, setBanner] = useState<ErrorBannerState | null>(null);
  const [conflict, setConflict] = useState(false);

  const { data: rawGroups, isLoading, isError } = useQuery({
    queryKey: GROUPS_KEY,
    queryFn: adminCakeOptionGroups.getAll,
  });
  const { data: rawCategories } = useQuery({
    queryKey: CATEGORIES_KEY,
    queryFn: adminCategories.getAll,
  });
  const groups = Array.isArray(rawGroups) ? rawGroups : [];
  const categoryNodes = useMemo(
    () => buildCategoryTree(Array.isArray(rawCategories) ? rawCategories : []),
    [rawCategories]
  );
  const categoryName = useMemo(() => {
    const m = new Map<number, string>();
    categoryNodes.forEach(c => m.set(c.id, c.name));
    return m;
  }, [categoryNodes]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: GROUPS_KEY });

  const closeGroupModal = () => {
    setGroupModalOpen(false);
    setEditingGroup(null);
    setGroupForm(BLANK_GROUP_FORM);
  };
  const closeItemModal = () => {
    setItemModalGroup(null);
    setEditingItem(null);
    setItemForm(BLANK_ITEM_FORM);
  };

  // A stale-save 409 reloads the list and tells the admin to re-apply the edit.
  const handleSaveError = (err: any) => {
    if (isStaleConflict(err)) {
      setConflict(true);
      closeGroupModal();
      closeItemModal();
      invalidate();
    }
  };

  const createGroup = useMutation({
    mutationFn: (data: CakeOptionGroupInput) => adminCakeOptionGroups.create(data),
    onSuccess: () => { invalidate(); closeGroupModal(); },
  });
  const updateGroup = useMutation({
    mutationFn: ({ id, data }: { id: number; data: CakeOptionGroupInput }) =>
      adminCakeOptionGroups.update(id, data),
    onSuccess: () => { invalidate(); closeGroupModal(); },
    onError: handleSaveError,
  });
  const deleteGroup = useMutation({
    mutationFn: (id: number) => adminCakeOptionGroups.delete(id),
    onSuccess: () => { setBanner(null); invalidate(); },
    onError: (err: any) =>
      setBanner({ raw: getErrorDetail(err), fallbackKey: 'admin.cakeOptions.groupDeleteError' }),
  });
  const createItem = useMutation({
    mutationFn: ({ groupId, data }: { groupId: number; data: CakeOptionItemInput }) =>
      adminCakeOptionItems.create(groupId, data),
    onSuccess: () => { invalidate(); closeItemModal(); },
  });
  const updateItem = useMutation({
    mutationFn: ({ id, data }: { id: number; data: CakeOptionItemInput }) =>
      adminCakeOptionItems.update(id, data),
    onSuccess: () => { invalidate(); closeItemModal(); },
    onError: handleSaveError,
  });
  const deleteItem = useMutation({
    mutationFn: (id: number) => adminCakeOptionItems.delete(id),
    onSuccess: () => { setBanner(null); invalidate(); },
    onError: (err: any) =>
      setBanner({ raw: getErrorDetail(err), fallbackKey: 'admin.cakeOptions.itemDeleteError' }),
  });
  const toggleItem = useMutation({
    mutationFn: (id: number) => adminCakeOptionItems.toggle(id),
    onSuccess: () => { setBanner(null); invalidate(); },
    onError: (err: any) =>
      setBanner({ raw: getErrorDetail(err), fallbackKey: 'admin.cakeOptions.itemToggleError' }),
  });

  const openAddGroup = () => {
    createGroup.reset(); updateGroup.reset();
    setEditingGroup(null);
    setGroupForm({ ...BLANK_GROUP_FORM, sort_order: groups.length });
    setConflict(false);
    setGroupModalOpen(true);
  };
  const openEditGroup = (g: CakeOptionGroup) => {
    createGroup.reset(); updateGroup.reset();
    setEditingGroup(g);
    setGroupForm({
      name: g.name,
      name_zh: g.name_zh ?? '',
      selection_mode: g.selection_mode,
      required: g.required,
      sort_order: g.sort_order,
      active: g.active,
      category_ids: g.category_ids,
      updated_at: g.updated_at ?? null,
    });
    setConflict(false);
    setGroupModalOpen(true);
  };
  const openAddItem = (g: CakeOptionGroup) => {
    createItem.reset(); updateItem.reset();
    setEditingItem(null);
    setItemForm({ ...BLANK_ITEM_FORM, sort_order: g.items.length });
    setItemModalGroup(g);
    setConflict(false);
  };
  const openEditItem = (g: CakeOptionGroup, item: CakeOptionItem) => {
    createItem.reset(); updateItem.reset();
    setEditingItem(item);
    setItemForm({
      name: item.name,
      name_zh: item.name_zh ?? '',
      default_price: String(item.default_price),
      sort_order: item.sort_order,
      active: item.active,
      updated_at: item.updated_at ?? null,
    });
    setItemModalGroup(g);
    setConflict(false);
  };

  const handleGroupSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const { updated_at, ...rest } = groupForm;
    const payload: CakeOptionGroupInput = { ...rest, name_zh: rest.name_zh.trim() || null };
    if (editingGroup) {
      updateGroup.mutate({ id: editingGroup.id, data: { ...payload, updated_at } });
    } else {
      createGroup.mutate(payload);
    }
  };
  const handleItemSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!itemModalGroup) return;
    const { updated_at, default_price, ...rest } = itemForm;
    const payload: CakeOptionItemInput = {
      ...rest,
      name_zh: rest.name_zh.trim() || null,
      default_price: parseFloat(default_price) || 0,
    };
    if (editingItem) {
      updateItem.mutate({ id: editingItem.id, data: { ...payload, updated_at } });
    } else {
      createItem.mutate({ groupId: itemModalGroup.id, data: payload });
    }
  };

  const toggleCategory = (id: number) =>
    setGroupForm(prev => ({
      ...prev,
      category_ids: prev.category_ids.includes(id)
        ? prev.category_ids.filter(c => c !== id)
        : [...prev.category_ids, id],
    }));

  const groupSaving = createGroup.isPending || updateGroup.isPending;
  const itemSaving = createItem.isPending || updateItem.isPending;
  const bannerText = resolveErrorBanner(banner, t);

  const errorText = (err: any) => getErrorDetail(err) || t('admin.cakeOptions.genericError');

  if (isLoading) {
    return <LoadingContainer>...</LoadingContainer>;
  }
  if (isError) {
    return (
      <Container>
        <Header><Title>{t('admin.cakeOptions.title')}</Title></Header>
        <ErrorMessage>{t('admin.cakeOptions.loadError')}</ErrorMessage>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>{t('admin.cakeOptions.title')}</Title>
        <Button data-testid="add-cake-option-group" onClick={openAddGroup}>
          <FiPlus size={14} />
          {t('admin.cakeOptions.addGroupButton')}
        </Button>
      </Header>

      {conflict && (
        <ErrorBanner role="alert" data-testid="cake-options-conflict">
          <span>{t('admin.cakeOptions.conflictError')}</span>
          <DismissButton type="button" aria-label={t('admin.cakeOptions.dismissNoticeLabel')} onClick={() => setConflict(false)}>×</DismissButton>
        </ErrorBanner>
      )}
      {bannerText && (
        <ErrorBanner role="alert" data-testid="cake-options-error">
          <span>{bannerText}</span>
          <DismissButton type="button" aria-label={t('admin.cakeOptions.dismissNoticeLabel')} onClick={() => setBanner(null)}>×</DismissButton>
        </ErrorBanner>
      )}

      {groups.map(group => {
        const names = group.category_ids.map(id => categoryName.get(id) ?? `#${id}`);
        const items = [...group.items].sort((a, b) => a.sort_order - b.sort_order || a.id - b.id);
        return (
          <Section key={group.id} data-testid={`cake-option-group-${group.id}`}>
            <SectionHeader>
              <SectionTitleRow>
                <SectionTitle>{display(group.name, group.name_zh)}</SectionTitle>
                <Badge $tone="blue">
                  {group.selection_mode === 'multiple' ? t('admin.cakeOptions.modeMultiple') : t('admin.cakeOptions.modeSingle')}
                </Badge>
                {group.required && <Badge $tone="amber">{t('admin.cakeOptions.requiredBadge')}</Badge>}
                {!group.active && <Badge>{t('admin.cakeOptions.inactiveBadge')}</Badge>}
                <Badge>
                  {t(items.length === 1 ? 'admin.cakeOptions.itemCountSingular' : 'admin.cakeOptions.itemCountPlural', { count: items.length })}
                </Badge>
              </SectionTitleRow>
              <HeaderRight>
                <IconButton $variant="edit" title={t('admin.cakeOptions.editGroupTooltip')} aria-label={t('admin.cakeOptions.editGroupTooltip')} onClick={() => openEditGroup(group)}>
                  <FiEdit2 size={14} />
                </IconButton>
                <IconButton
                  $variant="delete"
                  title={t('admin.cakeOptions.deleteGroupTooltip')}
                  aria-label={t('admin.cakeOptions.deleteGroupTooltip')}
                  disabled={deleteGroup.isPending}
                  onClick={() => {
                    if (window.confirm(t('admin.cakeOptions.deleteGroupConfirm'))) {
                      setBanner(null);
                      deleteGroup.mutate(group.id);
                    }
                  }}
                >
                  <FiTrash2 size={14} />
                </IconButton>
                <Button data-testid={`add-cake-option-item-${group.id}`} onClick={() => openAddItem(group)}>
                  <FiPlus size={14} />
                  {t('admin.cakeOptions.addItemButton')}
                </Button>
              </HeaderRight>
            </SectionHeader>
            <CategorySummary>
              {names.length > 0
                ? t('admin.cakeOptions.categoriesSummary', { names: names.join(', ') })
                : t('admin.cakeOptions.noCategoriesSummary')}
            </CategorySummary>
            <TableWrapper>
              <Table>
                <thead>
                  <tr>
                    <Th>{t('admin.cakeOptions.nameHeader')}</Th>
                    <Th>{t('admin.cakeOptions.defaultPriceHeader')}</Th>
                    <Th>{t('admin.cakeOptions.sortHeader')}</Th>
                    <Th>{t('admin.cakeOptions.activeHeader')}</Th>
                    <Th>{t('admin.cakeOptions.actionsHeader')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr><EmptyCell colSpan={5}>{t('admin.cakeOptions.emptyItemsState')}</EmptyCell></tr>
                  ) : (
                    items.map(item => (
                      <tr key={item.id}>
                        <Td>{display(item.name, item.name_zh)}</Td>
                        <Td>{formatCurrency(item.default_price)}</Td>
                        <Td>{item.sort_order}</Td>
                        <Td>
                          <input
                            type="checkbox"
                            checked={item.active}
                            disabled={toggleItem.isPending}
                            aria-label={item.active ? t('admin.cakeOptions.deactivateTooltip') : t('admin.cakeOptions.activateTooltip')}
                            data-testid={`toggle-cake-option-item-${item.id}`}
                            onChange={() => { setBanner(null); toggleItem.mutate(item.id); }}
                          />
                        </Td>
                        <Td>
                          <ActionCell>
                            <IconButton $variant="edit" title={t('admin.cakeOptions.editItemTooltip')} aria-label={t('admin.cakeOptions.editItemTooltip')} onClick={() => openEditItem(group, item)}>
                              <FiEdit2 size={14} />
                            </IconButton>
                            <IconButton
                              $variant="delete"
                              title={t('admin.cakeOptions.deleteItemTooltip')}
                              aria-label={t('admin.cakeOptions.deleteItemTooltip')}
                              disabled={deleteItem.isPending}
                              onClick={() => {
                                if (window.confirm(t('admin.cakeOptions.deleteItemConfirm'))) {
                                  setBanner(null);
                                  deleteItem.mutate(item.id);
                                }
                              }}
                            >
                              <FiTrash2 size={14} />
                            </IconButton>
                          </ActionCell>
                        </Td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </TableWrapper>
          </Section>
        );
      })}

      {groups.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#9ca3af', background: 'white', borderRadius: '12px' }}>
          <p>{t('admin.cakeOptions.emptyGroupsState')}</p>
        </div>
      )}

      {groupModalOpen && (
        <Modal role="dialog" aria-modal="true" data-testid="cake-option-group-modal">
          <ModalContent>
            <ModalHeader>
              <ModalTitle>
                {editingGroup
                  ? t('admin.cakeOptions.modalEditGroupTitle', { name: editingGroup.name })
                  : t('admin.cakeOptions.modalAddGroupTitle')}
              </ModalTitle>
              <CloseButton type="button" onClick={closeGroupModal}>×</CloseButton>
            </ModalHeader>
            <Form onSubmit={handleGroupSubmit}>
              <FormRow>
                <FormGroup>
                  <Label htmlFor="cc-group-name">{t('admin.cakeOptions.nameLabel')}</Label>
                  <Input id="cc-group-name" type="text" maxLength={CC_NAME_MAX_LENGTH} required autoFocus value={groupForm.name}
                    placeholder={t('admin.cakeOptions.namePlaceholder')}
                    onChange={e => setGroupForm(prev => ({ ...prev, name: e.target.value }))} />
                </FormGroup>
                <FormGroup>
                  <Label htmlFor="cc-group-name-zh">{t('admin.cakeOptions.nameZhLabel')}</Label>
                  <Input id="cc-group-name-zh" type="text" maxLength={CC_NAME_MAX_LENGTH} value={groupForm.name_zh}
                    onChange={e => setGroupForm(prev => ({ ...prev, name_zh: e.target.value }))} />
                </FormGroup>
              </FormRow>
              <FormGroup>
                <Label htmlFor="cc-group-mode">{t('admin.cakeOptions.selectionModeLabel')}</Label>
                <Select id="cc-group-mode" value={groupForm.selection_mode}
                  onChange={e => setGroupForm(prev => ({ ...prev, selection_mode: e.target.value as CakeOptionSelectionMode }))}>
                  <option value="single">{t('admin.cakeOptions.selectionModeSingleOption')}</option>
                  <option value="multiple">{t('admin.cakeOptions.selectionModeMultipleOption')}</option>
                </Select>
              </FormGroup>
              <FormGroup>
                <CheckboxLabel>
                  <input type="checkbox" checked={groupForm.required}
                    onChange={e => setGroupForm(prev => ({ ...prev, required: e.target.checked }))} />
                  {t('admin.cakeOptions.requiredLabel')}
                </CheckboxLabel>
              </FormGroup>
              <FormGroup>
                <Label htmlFor="cc-group-sort">{t('admin.cakeOptions.sortOrderLabel')}</Label>
                <Input id="cc-group-sort" type="number" min="0" value={groupForm.sort_order}
                  onChange={e => setGroupForm(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 0 }))} />
              </FormGroup>
              <FormGroup>
                <CheckboxLabel>
                  <input type="checkbox" checked={groupForm.active}
                    onChange={e => setGroupForm(prev => ({ ...prev, active: e.target.checked }))} />
                  {t('admin.cakeOptions.activeCheckboxLabel')}
                </CheckboxLabel>
              </FormGroup>
              <FormGroup role="group" aria-label={t('admin.cakeOptions.categoriesLabel')}>
                <Label as="span">{t('admin.cakeOptions.categoriesLabel')}</Label>
                <Hint data-testid="cake-options-inherit-hint">{t('admin.cakeOptions.inheritHint')}</Hint>
                <CategoryList>
                  {categoryNodes.length === 0 && (
                    <span style={{ color: '#9ca3af', fontSize: 13 }}>{t('admin.cakeOptions.noCategoriesAvailable')}</span>
                  )}
                  {categoryNodes.map(c => (
                    <CheckboxLabel key={c.id} style={{ paddingLeft: c.depth * 20 }}>
                      <input type="checkbox" checked={groupForm.category_ids.includes(c.id)}
                        onChange={() => toggleCategory(c.id)} />
                      {c.name}
                    </CheckboxLabel>
                  ))}
                </CategoryList>
              </FormGroup>
              {(createGroup.isError || (updateGroup.isError && !isStaleConflict(updateGroup.error))) && (
                <ErrorMessage>{errorText(createGroup.error || updateGroup.error)}</ErrorMessage>
              )}
              <ModalActions>
                <CancelButton type="button" onClick={closeGroupModal}>{t('admin.cakeOptions.cancelButton')}</CancelButton>
                <Button type="submit" disabled={groupSaving}>
                  {groupSaving
                    ? (editingGroup ? t('admin.cakeOptions.savingButton') : t('admin.cakeOptions.addingButton'))
                    : (editingGroup ? t('admin.cakeOptions.saveChangesButton') : t('admin.cakeOptions.addSubmitGroupButton'))}
                </Button>
              </ModalActions>
            </Form>
          </ModalContent>
        </Modal>
      )}

      {itemModalGroup && (
        <Modal role="dialog" aria-modal="true" data-testid="cake-option-item-modal">
          <ModalContent>
            <ModalHeader>
              <ModalTitle>
                {editingItem
                  ? t('admin.cakeOptions.modalEditItemTitle', { name: editingItem.name })
                  : t('admin.cakeOptions.modalAddItemTitle', { groupName: itemModalGroup.name })}
              </ModalTitle>
              <CloseButton type="button" onClick={closeItemModal}>×</CloseButton>
            </ModalHeader>
            <Form onSubmit={handleItemSubmit}>
              <FormRow>
                <FormGroup>
                  <Label htmlFor="cc-item-name">{t('admin.cakeOptions.nameLabel')}</Label>
                  <Input id="cc-item-name" type="text" maxLength={CC_NAME_MAX_LENGTH} required autoFocus value={itemForm.name}
                    onChange={e => setItemForm(prev => ({ ...prev, name: e.target.value }))} />
                </FormGroup>
                <FormGroup>
                  <Label htmlFor="cc-item-name-zh">{t('admin.cakeOptions.nameZhLabel')}</Label>
                  <Input id="cc-item-name-zh" type="text" maxLength={CC_NAME_MAX_LENGTH} value={itemForm.name_zh}
                    onChange={e => setItemForm(prev => ({ ...prev, name_zh: e.target.value }))} />
                </FormGroup>
              </FormRow>
              <FormGroup>
                <Label htmlFor="cc-item-price">{t('admin.cakeOptions.defaultPriceLabel')}</Label>
                <Input id="cc-item-price" type="number" step="0.01" value={itemForm.default_price}
                  onChange={e => setItemForm(prev => ({ ...prev, default_price: e.target.value }))} />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="cc-item-sort">{t('admin.cakeOptions.sortOrderLabel')}</Label>
                <Input id="cc-item-sort" type="number" min="0" value={itemForm.sort_order}
                  onChange={e => setItemForm(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 0 }))} />
              </FormGroup>
              <FormGroup>
                <CheckboxLabel>
                  <input type="checkbox" checked={itemForm.active}
                    onChange={e => setItemForm(prev => ({ ...prev, active: e.target.checked }))} />
                  {t('admin.cakeOptions.activeCheckboxLabel')}
                </CheckboxLabel>
              </FormGroup>
              {(createItem.isError || (updateItem.isError && !isStaleConflict(updateItem.error))) && (
                <ErrorMessage>{errorText(createItem.error || updateItem.error)}</ErrorMessage>
              )}
              <ModalActions>
                <CancelButton type="button" onClick={closeItemModal}>{t('admin.cakeOptions.cancelButton')}</CancelButton>
                <Button type="submit" disabled={itemSaving}>
                  {itemSaving
                    ? (editingItem ? t('admin.cakeOptions.savingButton') : t('admin.cakeOptions.addingButton'))
                    : (editingItem ? t('admin.cakeOptions.saveChangesButton') : t('admin.cakeOptions.addSubmitItemButton'))}
                </Button>
              </ModalActions>
            </Form>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
};

export default AdminCakeOptions;
